import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import prettyBytes from 'pretty-bytes';
import { ArrowRight, FileText, Package } from 'lucide-react';
import { toast } from 'sonner';
import type { PortalIssue, Upload, UploadStatus } from '@raptor/shared';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { IssueTypeBadge } from '@/components/IssueTypeBadge';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { useIssues } from '@/features/issues/api';
import { useUploads, useDownloadUrl } from '@/features/uploads/api';
import { useReleases } from '@/features/releases/api';
import { useAuth } from '@/lib/auth/useAuth';
import { isDataUploadsEnabled } from '@/lib/features';
import type { PortalRelease } from '@/features/releases/types';

const RECENT_LIMIT = 5;

export function Dashboard() {
  const { user } = useAuth();
  const greetingName = user?.name ?? user?.username ?? 'there';

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <PageHeader
        title={<span data-testid="dashboard-heading">Dashboard</span>}
        subtitle={<span data-testid="dashboard-greeting">Welcome back, {greetingName}.</span>}
      />

      <div
        className={
          isDataUploadsEnabled() ? 'grid gap-4 md:grid-cols-3' : 'grid gap-4 md:grid-cols-2'
        }
      >
        <RecentIssuesCard />
        {isDataUploadsEnabled() && <RecentUploadsCard />}
        <LatestReleaseCard />
      </div>
    </div>
  );
}

function RecentIssuesCard() {
  const issues = useIssues({ limit: RECENT_LIMIT });

  return (
    <DashboardCard
      title="Recent issues"
      icon={<FileText className="h-4 w-4" aria-hidden="true" />}
      viewAllHref="/issues"
      viewAllLabel="View all"
    >
      {issues.isLoading && <SkeletonRows />}
      {issues.isError && <CardError text="Couldn't load issues." />}
      {issues.isSuccess && (issues.data?.items.length ?? 0) === 0 && (
        <CardEmpty>
          No issues yet.{' '}
          <Link to="/issues/new" className="text-primary hover:underline">
            Submit your first issue.
          </Link>
        </CardEmpty>
      )}
      {issues.isSuccess && issues.data && issues.data.items.length > 0 && (
        <ul className="divide-y" data-testid="recent-issues-list">
          {issues.data.items.map((issue) => (
            <IssueLine key={issue.id} issue={issue} />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

function RecentUploadsCard() {
  const uploads = useUploads({ limit: RECENT_LIMIT });
  const downloader = useDownloadUrl();

  const handleDownload = async (upload: Upload) => {
    if (upload.status !== 'complete') return;
    try {
      const { url } = await downloader.fetchUrl(upload.id);
      window.location.href = url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed');
    }
  };

  return (
    <DashboardCard
      title="Recent uploads"
      icon={<Package className="h-4 w-4" aria-hidden="true" />}
      viewAllHref="/uploads"
      viewAllLabel="View all"
    >
      {uploads.isLoading && <SkeletonRows />}
      {uploads.isError && <CardError text="Couldn't load uploads." />}
      {uploads.isSuccess && (uploads.data?.items.length ?? 0) === 0 && (
        <CardEmpty>
          No uploads yet.{' '}
          <Link to="/uploads/new" className="text-primary hover:underline">
            Upload your first file.
          </Link>
        </CardEmpty>
      )}
      {uploads.isSuccess && uploads.data && uploads.data.items.length > 0 && (
        <ul className="divide-y" data-testid="recent-uploads-list">
          {uploads.data.items.map((upload) => (
            <UploadLine
              key={upload.id}
              upload={upload}
              onClick={() => void handleDownload(upload)}
            />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

function LatestReleaseCard() {
  const releases = useReleases({ limit: 1 });
  const latest = releases.data?.items[0];

  return (
    <DashboardCard
      title="Latest release"
      icon={<Package className="h-4 w-4" aria-hidden="true" />}
      viewAllHref="/releases"
      viewAllLabel="View all"
    >
      {releases.isLoading && <SkeletonRows count={2} />}
      {releases.isError && <CardError text="Couldn't load releases." />}
      {releases.isSuccess && !latest && <CardEmpty>No releases yet.</CardEmpty>}
      {releases.isSuccess && latest && <LatestReleaseBlock release={latest} />}
    </DashboardCard>
  );
}

function DashboardCard({
  title,
  icon,
  viewAllHref,
  viewAllLabel,
  children,
}: {
  title: string;
  icon: ReactNode;
  viewAllHref: string;
  viewAllLabel: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            {icon}
            {title}
          </CardTitle>
          <Link
            to={viewAllHref}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            {viewAllLabel}
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function SkeletonRows({ count = RECENT_LIMIT }: { count?: number }) {
  return (
    <div className="space-y-2" data-testid="dashboard-skeletons">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
  );
}

function CardError({ text }: { text: string }) {
  return <div className="py-2 text-sm text-muted-foreground">{text}</div>;
}

function CardEmpty({ children }: { children: ReactNode }) {
  return <div className="py-2 text-sm text-muted-foreground">{children}</div>;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function IssueLine({ issue }: { issue: PortalIssue }) {
  return (
    <li className="py-2">
      <Link
        to={`/issues/${issue.id}`}
        className="block rounded-sm hover:bg-muted/60 focus:bg-muted/60"
        data-testid={`dashboard-issue-${issue.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium" title={issue.title}>
            {truncate(issue.title, 60)}
          </span>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(issue.createdAt), { addSuffix: true })}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <IssueTypeBadge type={issue.issueType} />
          <StatusBadge
            variant={issue.status === 'open' ? 'success' : 'neutral'}
            label={issue.status}
            data-status={issue.status}
          />
        </div>
      </Link>
    </li>
  );
}

function UploadLine({ upload, onClick }: { upload: Upload; onClick: () => void }) {
  const canDownload = upload.status === 'complete';
  return (
    <li className="py-2">
      <button
        type="button"
        onClick={onClick}
        disabled={!canDownload}
        className="block w-full rounded-sm text-left enabled:hover:bg-muted/60 disabled:cursor-default"
        data-testid={`dashboard-upload-${upload.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium" title={upload.originalFilename}>
            {truncate(upload.originalFilename, 60)}
          </span>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(upload.uploadedAt), { addSuffix: true })}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <Badge
            data-type={upload.uploadType}
            className={
              upload.uploadType === 'recording'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-purple-100 text-purple-700'
            }
          >
            {upload.uploadType}
          </Badge>
          <UploadStatusBadge status={upload.status} />
          <span className="text-xs text-muted-foreground">{prettyBytes(upload.sizeBytes)}</span>
        </div>
      </button>
    </li>
  );
}

function UploadStatusBadge({ status }: { status: UploadStatus }) {
  const colour: Record<UploadStatus, string> = {
    pending: 'bg-gray-100 text-gray-700',
    complete: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    expired: 'bg-gray-100 text-gray-500 line-through',
  };
  return (
    <Badge data-status={status} className={colour[status]}>
      {status}
    </Badge>
  );
}

// Single sentence of the body, trimmed. Defined as everything before the first
// '. ', '\n', or '? '/'! ' — keeps the dashboard compact when release notes are
// long Markdown documents.
function firstSentence(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^(.+?[.?!])(\s|$)/);
  const firstLine = trimmed.split(/\r?\n/, 1)[0];
  return match ? match[1] : firstLine;
}

function LatestReleaseBlock({ release }: { release: PortalRelease }) {
  const summary = firstSentence(release.body);
  return (
    <Link
      to={`/releases/${encodeURIComponent(release.repoName)}`}
      className="block rounded hover:bg-slate-50"
      data-testid={`dashboard-release-${release.repoName}-${release.tag}`}
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {release.productDisplayName}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className="font-mono text-sm">{release.tag}</span>
        {release.isPrerelease && <Badge className="bg-amber-100 text-amber-800">Prerelease</Badge>}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Released {formatDistanceToNow(new Date(release.publishedAt), { addSuffix: true })}
      </div>
      {summary && <p className="mt-2 text-sm text-foreground/80">{truncate(summary, 140)}</p>}
    </Link>
  );
}
